import {defineStore} from 'pinia';
import { v4 as uuidv4 } from 'uuid';
import {QUIZTYPE} from "@/consts/answer_column";
import {SUBJECT_MAP} from "@/consts/subject";
import { BreakDataCollection, WorkBreakData } from '@/models/studyBreakData';
import {MISSIONKIND_SORT} from "@/consts/mission";
import {
    IWork,
    IChapterProgress,
    INextQuestionSet,
    IReviewTag,
    ISection,
    ISectionProgress,
    IQuestion, IQuestionProgress, IQuestionSet
} from "@/consts/interfaces";
import local_time from "@/util/datetime";
import redirect_error_page from "@/util/redirect_error_page"
import { useVersionStore } from './version_store';
import apiRequest from '@/util/api-request';
import { AxiosRequestConfig } from 'axios';
import { localStorageService } from '@/util/local_storage_service'
import { useAccountStore } from '@/store/student/account_store'

interface IWorks {
    work_list: Array<IWork>;
    work_by_sub_name: {[id: string]: Array<IWork>};
    work_by_id: {[id: string]: {subject: string, subject_field: string, academic_year: number}};
    selected_work: Array<IWork>;
    sub_field: string;
    chapter_progress: {[id: string] : IChapterProgress};
    next_question_set: INextQuestionSet | null;
    selected_work_index: number;
    review_tags: Array<IReviewTag>;
    section_list: Array<ISection>;
    section_progress: {[id: string] : ISectionProgress};
    question_list: Array<IQuestion>;
    question_progress: {[id: string] : IQuestionProgress};
    question_ids: Array<number>;
    selected_question_set: {[id: string] : IQuestionSet};
    q_idx: number;
    question_correct_wrong_list: Array<{ result: boolean|null, progress:number }>;
    initial_answer: Array<any>;
    start_date: string;
    learning_start_page: any;
    remain_question: Array<IQuestion>;
    is_review_question: boolean;
    is_mission_question: boolean;
    mission: {uuid: string, kind: number} | null;
    answered_question_ids: Array<number>;
    total_num_of_question: number;
    is_unanswered: boolean;
    latest_question_histories: Array<any>;
    is_prev_question_history: boolean;
    latest_part:{id:number};
    latest_section:{id:number};
    animation_id: number;
    loading: boolean;
    is_prev_question_answer_button: boolean;
    is_search_question_by_id: boolean;
    is_learning_page: boolean;
    is_learning_mode: boolean;
    transit_time: number;
    is_disabled_button: boolean;
    uuid: string | null;
    accordion_by_section_id: {[id: number] : boolean};
    is_break_time_modal: boolean;
    composition_in_progress: boolean;
    is_valid_works: boolean;
    is_chapter_review: boolean;
    study_result_ids: Array<string>;
    is_save_result_in_progress: boolean;
    study_break_data: BreakDataCollection;
    last_learning_question_set_id: number | null;
    is_wrongly_answered: boolean;
}


export const useWorkStore = defineStore('workStore', {
    // state
    state: (): IWorks => {
        return {
            work_list: [],
            work_by_sub_name: {},
            work_by_id: {},
            selected_work: [],
            sub_field: '',
            chapter_progress: {},
            next_question_set: null,
            selected_work_index: 0,
            review_tags: [],
            section_list: [],
            section_progress: {},
            question_list: [],
            question_progress: {},
            question_ids: [],
            selected_question_set: {},
            q_idx: 0,
            question_correct_wrong_list: [],
            initial_answer: [],
            start_date: "",
            learning_start_page: null,
            remain_question: [],
            is_review_question: false,
            is_mission_question: false,
            mission: null,
            answered_question_ids: [],
            total_num_of_question: 0,
            is_unanswered: false,
            latest_question_histories: [],
            is_prev_question_history: false,
            latest_part: {
                id: 0
            },
            latest_section: {
                id: 0
            },
            animation_id: 0,
            loading: false,
            is_prev_question_answer_button: false,
            is_search_question_by_id: false,
            is_learning_page: false, //S3-7~S3-9　学習ページのみ
            is_learning_mode: false, //S3-7~S3-10　学習のsequenceが終わるまで
            transit_time: 0,
            is_disabled_button: false, // NFR-3683 「次の問題へ」「答え合わせ」「OXを決定」活性化・非活性化状態フラグ
            uuid: null,
            accordion_by_section_id: {}, // NFR-3703, save grandchid selection state
            is_break_time_modal: false, // NFR-4415 休憩モーダル表示非表示フラグ（save_resultレスポンスのis_break_timeから取得）
            composition_in_progress: false, // NFR-4550 数学・理科で日本語入力中かをチェックするフラグ
            is_valid_works: false, // NFR-4440 ワーク/ワークAPIの値が取得されたかどうか確認するフラグ
            is_chapter_review: false, // Identify whether it chapter or section review from answer page(NFR-5155)
            study_result_ids: [], // NFR-5283 uuid_questionsetid 組み合わせの識別子を格納
            is_save_result_in_progress: false, // NFR-5249 save_result apiが進行中かどうかチェックするフラグ
            study_break_data: {}, // NFR-5116 save break data because of post process of data
            last_learning_question_set_id: null, // NFR-5456: to tracking last learning question_set 
            is_wrongly_answered: false // NFR-5442 S3-10から間違えた問題だけ再チャレンジを解く時、識別するフラグ
        }
    },
    // getters
    getters: {
        works: (state) => {
            return state.work_list
        },
        workBySubName: (state) => {
            return state.work_by_sub_name
        },
        workById: (state) => {
            return state.work_by_id
        },
        selectedWork: (state) => {
            return state.selected_work
        },
        subField: (state) => {
            return state.sub_field
        },
        chapterProgress: (state) => {
            return state.chapter_progress
        },
        nextQuestionSet: (state) => {
            return state.next_question_set
        },
        selectedWorkIndex: (state) => {
            return state.selected_work_index
        },
        selectedWorkId: (state) => {
            return state.selected_work[state.selected_work_index].id
        },
        reviewTags: (state) => {
            return state.review_tags
        },
        sections: (state) => {
            return state.section_list
        },
        sectionProgress: (state) => {
            return state.section_progress
        },
        questions: (state) => {
            return state.question_list
        },
        questionProgress: (state) => {
            return state.question_progress
        },
        questionIds: (state) => {
            return state.question_ids
        },
        selectedQuestionSet: (state) => {
            return state.selected_question_set
        },
        questionIndex: (state) => {
            return state.q_idx
        },
        questionCorrectWrongList: (state) => {
            return state.question_correct_wrong_list
        },
        initialAnswer: (state) => {
            return state.initial_answer
        },
        learningStartPage: (state) => {
            return state.learning_start_page
        },
        remainQuestion: (state) => {
            return state.remain_question
        },
        isReviewQuestion: (state) => {
            return state.is_review_question
        },
        isMissionQuestion: (state) => {
            return state.is_mission_question
        },
        learningMission: (state) => {
          return state.mission
        },
        answeredQuestionIds: (state) => {
            return state.answered_question_ids
        },
        totalNumOfQuestion: (state) => {
            return state.total_num_of_question
        },
        isUnanswered: (state) => {
            return state.is_unanswered
        },
        latestQuestionHistories: (state) => {
            return state.latest_question_histories
        },
        isPrevQuestionHistory: (state) => {
            return state.is_prev_question_history
        },
        latestPart: (state) => {
            return state.latest_part
        },
        latestSection: (state) => {
            return state.latest_section
        },
        animationId: (state) => {
            return state.animation_id
        },
        isPrevQuestionAnswerButton: (state) => {
            return state.is_prev_question_answer_button
        },   
        subjectField: (state) => {
          return state.selected_work[state.selected_work_index]?.subject_field
        },
        isSearchQuestionById: (state) => {
            return state.is_search_question_by_id
        },
        isLearningPage: (state) => {
            return state.is_learning_page
        },
        isLearningMode: (state) => {
            return state.is_learning_mode
        },
        isDisabledButton: (state) => {
            return state.is_disabled_button
        },
        transitTime: (state) => {
            return state.transit_time
        },
        accordionBySectionId: (state) => {
            return state.accordion_by_section_id
        },
        isBreakTimeModal: (state) => {
            return state.is_break_time_modal
        },
        compositionInProgress: (state) => {
            return state.composition_in_progress
        },
        isValidWorks: (state) => {
            return state.is_valid_works
        },
        isChapterReview: (state) => {
            return state.is_chapter_review
        },
        isInterrupt: (state) => {
            return (!state.is_review_question && !state.is_mission_question && !state.is_prev_question_history) || (state.mission && state.mission.kind == MISSIONKIND_SORT.NEW)
        },
        interruptBreakData: (state) => {
            return state.study_break_data[state.selected_work[state.selected_work_index].id]?.break_data || null
        },
        interruptTemporaryData: (state) => {
            return state.study_break_data[state.selected_work[state.selected_work_index]?.id]?.temporary_data || null
        },
        isWronglyAnswered: (state) => {
            return state.is_wrongly_answered
        }
    },
    // actions
    actions: {
        async fetchWorks() {
            const versionStore = useVersionStore()
            const accountStore = useAccountStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'get',
                    url: `/student/api/work/work?use_cache=${!accountStore.isDebug}`,
                };
                const works = await apiRequest(config);
                works.data.works.sort(function(a:IWork, b:IWork){
                    return a.academic_year - b.academic_year
                })
                let work_by_sub_name: {[id: string]: Array<IWork>} = {}
                let work_by_id: {[id: string]: {subject: string, subject_field: string, academic_year: number}} = {}
                for (let work of works.data.works) {
                    work.select = false
                    if(SUBJECT_MAP[work.subject.name] in work_by_sub_name){
                        work_by_sub_name[SUBJECT_MAP[work.subject.name]].push(work)
                    }else{
                        work_by_sub_name[SUBJECT_MAP[work.subject.name]] = [work]
                    }
                    work_by_id[work.id] = {subject: work.subject.name , subject_field: work.subject_field, academic_year: work.academic_year}
                }
                this.work_by_id = work_by_id
                if(work_by_sub_name[SUBJECT_MAP["社会"]]){
                    let chiri:Array<IWork>= []
                    let rekishi:Array<IWork> = []
                    let komin:Array<IWork> = []
                    // set shakai order 地理1>地理2>...>歴史1>歴史2.3>公民
                    for(let item of work_by_sub_name[SUBJECT_MAP["社会"]]){
                        switch(item.subject_field){
                            case '地理':
                                chiri.push(item)
                                break;
                            case '歴史':
                                rekishi.push(item)
                                break;
                            case '公民':
                                komin.push(item)
                                break;
                        }
                    }
                    work_by_sub_name[SUBJECT_MAP["社会"]] = chiri.concat(rekishi, komin)
                }
                this.work_by_sub_name = work_by_sub_name
                this.work_list = works.data.works
                this.is_valid_works = true
                versionStore.saveAppVersion(works.headers["x-app-version"])
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        async fetchChapterProgress(payload:{work_id: number, animation?: boolean}){
            const versionStore = useVersionStore()
            try{
                const config: AxiosRequestConfig = {
                    method: 'post',
                    url: '/student/api/work_progress/work',
                    data: payload,
                };
                const progress = await apiRequest(config);
                let new_progress:{[id: string] : IChapterProgress} = {}
                for(const item of progress.data.chapter_progress){
                    new_progress[item.chapter_id] = item
                }
                if(payload.animation){
                    return new_progress
                }else{
                    this.chapter_progress = new_progress
                }
                versionStore.saveAppVersion(progress.headers["x-app-version"])
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        async fetchQuestions(payload){
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'post',
                    url: '/student/api/work/question',
                    data: payload,
                };
                const question = await apiRequest(config);
                let question_ids: Array<number> = question.data.questions.map(({ id }) => id);
                this.question_ids = question_ids
                this.question_list = question.data.questions
                versionStore.saveAppVersion(question.headers["x-app-version"])
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        async fetchNextQuestionSet(payload) {
            const versionStore = useVersionStore()
            try{
                const config: AxiosRequestConfig = {
                    method: 'post',
                    url: '/student/api/work_progress/next_question_set',
                    data: payload,
                };
                const nextQuestionSet = await apiRequest(config);
                this.next_question_set = nextQuestionSet.data.next_question_set
                versionStore.saveAppVersion(nextQuestionSet.headers["x-app-version"])
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        async fetchReviewTag(payload){
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'post',
                    url: '/student/api/review_tag/question',
                    data: payload,
                };
                const reviewTag = await apiRequest(config);
                this.review_tags = reviewTag.data.review_tags
                versionStore.saveAppVersion(reviewTag.headers["x-app-version"])
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        async fetchSectionProgress(payload){
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'post',
                    url: '/student/api/work_progress/section',
                    data: payload,
                };
                const progress = await apiRequest(config);
                let new_progress:{[id: string] : ISectionProgress} = {}
                for(const item of progress.data.sections){
                    new_progress[item.id] = item
                }
                if(payload.animation){
                    return new_progress
                }else{
                   this.section_progress = new_progress
                }
                versionStore.saveAppVersion(progress.headers["x-app-version"])
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        async fetchQuestionProgress(payload){
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'post',
                    url: '/student/api/work_progress/question',
                    data: payload,
                };
                const progress = await apiRequest(config);
                let new_progress:{[id: string] : IQuestionProgress} = {}
                for(const item of progress.data.question_progress){
                    new_progress[item.id] = item
                }
                this.question_progress = new_progress
                versionStore.saveAppVersion(progress.headers["x-app-version"])
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        async fetchUpdateReviewTag(payload){
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'post',
                    url: '/student/api/review_tag/update',
                    data: payload,
                };
                const response = await apiRequest(config);
                versionStore.saveAppVersion(response.headers["x-app-version"])
                await this.fetchReviewTag({question_ids: this.question_ids})
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        initializeAnswer() {
            let question_histories: Array<any> = []
            let question_set_ids = new Set();
            for(let question of this.question_list){
                // NFR-5283 小問からquestion_set_idを取得
                question_set_ids.add(question.question_set_id);
                let quizzes:Array<any> = []
                for(let quiz of question.quizzes) {
                    if (quiz.answer_format == QUIZTYPE.WRITTEN_RANDOM_ORDER) {
                        let subQuizzes: Array<any> = []
                        for (let subQuiz of quiz.body.answersList) {
                            subQuizzes.push({
                                answer: "",
                                is_correct_answer: false
                            })
                        }
                        quizzes.push({
                            quiz_id: quiz.id,
                            answer: "",
                            sub_quizzes: subQuizzes,
                            is_correct_answer: false
                        })
                    } else {
                        quizzes.push({
                            quiz_id: quiz.id,
                            answer: "",
                            is_correct_answer: false
                        })
                    }
                }
                question_histories.push({
                    question_id: question.id,
                    study_start_datetime: "",
                    study_end_datetime: "",
                    is_correct_answer: false,
                    quiz_histories: quizzes
                })
            }
            this.initial_answer = question_histories
            this.start_date = local_time()
            this.uuid = uuidv4().replace(/-/g, "")
            // TODO: 不具合調査のため一旦残る。従来は改修する NFR-5283 study_result_idsの作成
            this.study_result_ids = Array.from(question_set_ids).map(id => `${this.uuid}_${id}`);
        },
        async fetchSaveStudyResult() {
            const date:string = local_time()
            const skipIndexes = []
            // NFR-5249 save_resultの処理が進行中である場合、処理を中断
            if(this.is_save_result_in_progress){
                return;
            }
            // NFR-5249 save_resultの処理が進行中であることを示すフラグを設定
            this.is_save_result_in_progress = true;
            // 問題の正誤リストの長さが回答リストの長さと同じである場合
            if (this.question_correct_wrong_list.length === this.initial_answer.length) {
                this.question_correct_wrong_list.forEach((item, index) => {
                    // 各項目が null で進捗が 1 の場合、スキップするインデックスに追加
                    if (item.result === null && item.progress === 1) {
                        skipIndexes.push(index);
                    }
                });
            }
            this.initial_answer.forEach((question, questionIndex) => {
                question.quiz_histories.forEach((quiz) => {
                    if (quiz.sub_quizzes) {
                        delete quiz.sub_quizzes;
                    }
                    // NFR-5190 スキップするインデックスに現在のインデックスが含まれる場合、クイズの回答を空に設定
                    if (skipIndexes.includes(questionIndex)) {
                        quiz.answer = "";
                    }
                });
                if(question.study_start_datetime.length === 0) {
                    question.study_start_datetime = local_time()
                }
                if(question.study_end_datetime.length === 0) {
                    question.study_end_datetime = local_time()
                }
            });
            let request_data = {
                study_start_datetime: this.start_date,
                study_end_datetime: date,
                is_review_question: this.isReviewQuestion,
                mission: this.mission,
                question_histories: this.initial_answer,
                study_result_ids: this.study_result_ids
            }
            const config: AxiosRequestConfig = {
                method: 'post',
                url: `/student/api/work_progress/save_result?v=${this.uuid}`,
                data: request_data,
            };
            try{
                const response = await apiRequest(config);
                this.is_break_time_modal = response.data.is_break_time
                return response
            }finally{
                // NFR-5249 処理が完了したらフラグをリセット
                this.is_save_result_in_progress = false
            }
        },
        async fetchQuestionSetProgress(payload){
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'post',
                    url: '/student/api/work_progress/question_set_progress',
                    data: payload,
                };
                const progress = await apiRequest(config);
                let answered_ids:Array<number> = []
                for(const item of progress.data.question_set_progresses){
                    answered_ids.push(...item.done_correctly_question_ids)
                }
                this.answered_question_ids = answered_ids
                versionStore.saveAppVersion(progress.headers["x-app-version"])
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        async fetchLatestQuestionHistories(payload){
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'post',
                    url: '/student/api/work_progress/latest_question_history',
                    data: payload,
                };
                const histories = await apiRequest(config);
                this.latest_question_histories = histories.data.question_histories
                versionStore.saveAppVersion(histories.headers["x-app-version"])
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        async fetchLatestPart(payload){
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'post',
                    url: '/student/api/work_progress/latest_part',
                    data: payload,
                };
                const last_part = await apiRequest(config);
                if(!!last_part.data.latest_part){
                    this.latest_part = last_part.data.latest_part
                }
                versionStore.saveAppVersion(last_part.headers["x-app-version"])
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        async fetchLatestSection(payload){
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'post',
                    url: '/student/api/work_progress/latest_section',
                    data: payload,
                };
                const last_section = await apiRequest(config);
                if(!!last_section.data.latest_section){
                    this.latest_section = last_section.data.latest_section
                }
                versionStore.saveAppVersion(last_section.headers["x-app-version"])
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        async fetchSearchQuestions(payload){
            const config: AxiosRequestConfig = {
                method: 'post',
                url: '/student/api/work/question',
                data: payload,
            };
            const response = await apiRequest(config);
            return response
        },
        async fetchLatestLearningWorkId(payload){
            try {
                const config: AxiosRequestConfig = {
                    method: 'post',
                    url: '/student/api/work/latest_learning_work_id',
                    data: payload,
                };
                const response = await apiRequest(config);
                return response
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        async fetchMathCalculation(payload){
            // payload must contains
            // {
            //     "111": { // quiz_idをキーにしたリクエストデータ
            //         "user_answer": "+\\frac{7}{3}", // latex形式のユーザー解答
            //         "quiz_answer": ["+\\frac{7}{3}", "+2\\frac{1}{3}"] // latex形式の正解
            //     },
            //     "112": { // 順不問記述式の場合、１個のquiz_idで複数のsub_quizがあるので構造は下のようになる。
            //         "user_answer": ["30", "2", "3"]
            //         "quiz_answer": [["1"], ["2"], ["3", "4"]],
            //     },
            // }
            try {
                const config: AxiosRequestConfig = {
                    method: 'post',
                    url: '/exercise/api/judge/math_calculate',
                    data: payload,
                };
                const response = await apiRequest(config);
                return response
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        saveSelectedWork(payload) {
            this.selected_work = payload
        },
        saveSubField(payload) {
            this.sub_field = payload
        },
        saveChapterProgress(payload) {
            this.chapter_progress = payload
        },
        saveNextQuestionSet(payload) {
            this.next_question_set = payload
        },
        saveSelectedWorkIndex(payload) {
            this.selected_work_index = payload
        },
        saveSectionProgress(payload) {
            this.section_progress = payload
        },
        saveQuestions(payload:Array<IQuestion>) {
            this.question_list = payload
        },
        saveQuestionIds(payload: Array<number>) {
            this.question_ids = payload
        },
        saveSelectedQuestionSet(payload) {
            this.selected_question_set = payload
        },
        saveQIdx(payload) {
            this.q_idx = payload
        },
        saveQuestionCorrectWrongList(payload) {
            this.question_correct_wrong_list = payload
        },
        saveInitializeAnswer(payload) {
            this.initial_answer = payload
            this.start_date = local_time()
        },
        saveAnswerValue(payload) {
            this.initial_answer[this.q_idx].quiz_histories[payload.id].answer = payload.text
            // NFR-5676: fetch selected work temporary data
            const tempData = this.interruptTemporaryData
            // NFR-5676: Check if the learning can be interrupted and temp data for current work
            if(this.isInterrupt && tempData) {
                // NFR-5116: update study break data
                tempData.saved_answers[this.q_idx].quiz_histories[payload.id].answer = payload.text
                localStorageService.saveStudyBreakData(this.study_break_data)
            }
        },
        saveRandomWrittenAnswerValue(payload) {
            this.initial_answer[this.q_idx].quiz_histories[payload.id].sub_quizzes[payload.sub_id].answer = payload.text
            // NFR-5676: fetch selected work temporary data
            const tempData = this.interruptTemporaryData
            // NFR-5676: Check if the learning can be interrupted and temp data for current work
            if(this.isInterrupt && tempData) {
                // NFR-5116: update study break data
                tempData.saved_answers[this.q_idx].quiz_histories[payload.id].sub_quizzes[payload.sub_id].answer = payload.text
                localStorageService.saveStudyBreakData(this.study_break_data)
                
            }
        },
        saveQuestionStudyStartTime() {
            if(this.initial_answer[this.q_idx])
            {
                const time_now = local_time()
                this.initial_answer[this.q_idx].study_start_datetime = time_now
                // NFR-5676: fetch selected work temporary data
                const tempData = this.interruptTemporaryData
                // NFR-5676: Check if the learning can be interrupted and temp data for current work
                if(this.isInterrupt && tempData){
                    // NFR-5116: update question study start time
                    tempData.saved_answers[this.q_idx].study_start_datetime = time_now
                    localStorageService.saveStudyBreakData(this.study_break_data)
                }
            }
        },
        saveQuestionStudyEndTime() {
            const time_now = local_time()
            this.initial_answer[this.q_idx].study_end_datetime = time_now
            // NFR-5676: fetch selected work temporary data
            const tempData = this.interruptTemporaryData
            // NFR-5676: Check if the learning can be interrupted and temp data for current work
            if(this.isInterrupt && tempData){
                // NFR-5116: update question study end time
                tempData.saved_answers[this.q_idx].study_end_datetime = time_now
                localStorageService.saveStudyBreakData(this.study_break_data)
            }
        },
        saveQuestionCorrectAnswer(payload) {
            this.initial_answer[this.q_idx].is_correct_answer = payload
            // NFR-5676: fetch selected work temporary data
            const tempData = this.interruptTemporaryData
            // NFR-5676: Check if the learning can be interrupted and temp data for current work
            if(this.isInterrupt && tempData) {
                // NFR-5116: update study break data
                tempData.saved_answers[this.q_idx].is_correct_answer = payload
                localStorageService.saveStudyBreakData(this.study_break_data)
            }
        },
        saveQuizCorrectAnswer(payload) {
            this.initial_answer[this.q_idx].quiz_histories[payload.id].is_correct_answer = payload.is_correct
            // NFR-5676: fetch selected work temporary data
            const tempData = this.interruptTemporaryData
            // NFR-5676: Check if the learning can be interrupted and temp data for current work
            if(this.isInterrupt && tempData) {
                // NFR-5116: update study break data
                tempData.saved_answers[this.q_idx].quiz_histories[payload.id].is_correct_answer = payload.is_correct
                localStorageService.saveStudyBreakData(this.study_break_data)
            }
        },
        saveRandomQuizCorrectAnswer(payload) {
            this.initial_answer[this.q_idx].quiz_histories[payload.id].is_correct_answer = payload.is_correct
            this.initial_answer[this.q_idx].quiz_histories[payload.id].sub_quizzes = payload.sub_quizzes
            this.initial_answer[this.q_idx].quiz_histories[payload.id].answer = payload.sub_quizzes.map(item=>item.answer).join("、")
            // NFR-5676: fetch selected work temporary data
            const tempData = this.interruptTemporaryData
            // NFR-5676: Check if the learning can be interrupted and temp data for current work
            if(this.isInterrupt && tempData){
                // NFR-5116: update study break data
                tempData.saved_answers[this.q_idx].quiz_histories[payload.id].is_correct_answer = payload.is_correct
                tempData.saved_answers[this.q_idx].quiz_histories[payload.id].sub_quizzes = payload.sub_quizzes
                tempData.saved_answers[this.q_idx].quiz_histories[payload.id].answer = payload.sub_quizzes.map(item=>item.answer).join("、")
                localStorageService.saveStudyBreakData(this.study_break_data)
            }
        },
        saveSingleQuestionCorrectWrongList(payload) {
            this.question_correct_wrong_list[this.q_idx] = payload
            // NFR-5676: fetch selected work temporary data
            const tempData = this.interruptTemporaryData
            // NFR-5676: Check if the learning can be interrupted and temp data for current work
            if(this.isInterrupt && tempData){
                // NFR-5116: update study break data
                tempData.correct_wrong_list[this.q_idx] = payload
                localStorageService.saveStudyBreakData(this.study_break_data)
            }
        },
        saveRemainQuestion(payload) {
            this.remain_question = payload
        },
        saveIsReviewQuestion(payload) {
            this.is_review_question = payload
        },
        saveIsChapterReview(payload) {
            this.is_chapter_review = payload
        },
        saveIsMissionQuestion(payload) {
            this.is_mission_question = payload
        },
        saveMission(payload) {
            this.mission = payload
        },
        saveTotalNumOfQuestion(payload) {
            this.total_num_of_question = payload
        },
        saveIsUnanswered(payload) {
            this.is_unanswered = payload
        },
        saveIsPrevQuestionHistory(payload) {
            this.is_prev_question_history = payload
        },
        saveLearningStartPage(payload) {
            this.learning_start_page = payload
        },
        saveAnimationId(payload) {
            this.animation_id = payload
        },
        saveLoading(payload) {
            this.loading = payload
        },
        saveIsPrevQuestionAnswerButton(payload) {
            this.is_prev_question_answer_button = payload
        },
        saveResolvedQuestionCorrectWrong(payload) {
            this.question_correct_wrong_list[payload] = {progress: 0, result: null}
        },
        saveIsSearchQuestionById(payload) {
            this.is_search_question_by_id = payload
        },
        saveIsLearningPage(payload) {
            this.is_learning_page = payload
        },
        saveIsLearningMode(payload) {
            this.is_learning_mode = payload
        },
        saveTransitTime(payload) {
            this.transit_time = payload
        },
        saveIsDisabledButton(payload) {
            this.is_disabled_button = payload
        },
        saveSelectedWorkSession(payload) {
            this.work_by_sub_name[payload["subject_name"]].forEach(item => item.select = false)
            this.work_by_sub_name[payload["subject_name"]][payload["index"]].select = true
        },
        saveAccordionBySectionId(payload:{section_id: number, value: boolean}) {
            this.accordion_by_section_id[payload.section_id] = payload.value
        },
        saveResolvedInitialAnswer(payload) {
            // reset already answered question with all quizes according to question index
            for(let quiz of this.initial_answer[payload].quiz_histories){
                quiz.answer = ""
                quiz.is_correct_answer = false
                if(quiz.sub_quizzes){
                for(let i=0; i<quiz.sub_quizzes.length; i++){
                    quiz.sub_quizzes[i].answer = ""
                    quiz.sub_quizzes[i].is_correct_answer = false
                }
                }
            }
        },
        saveSections(payload){
            this.section_list = payload
        },
        saveIsBreakTimeModal(payload){
            this.is_break_time_modal = payload
        },
        saveCompositionInProgress(payload){
            this.composition_in_progress = payload
        },
        saveLastLearningQuestionSetId(payload:number | null){
            this.last_learning_question_set_id = payload
        },
        resetChapterProgress(){
            this.chapter_progress = {}     
        },
        resetSectionProgress(){
            this.section_progress = {}
        },
        saveStudyBreakData(payload: WorkBreakData) {
            if(this.isInterrupt){
                if(payload.break_data?.question_set_id === payload.temporary_data?.question_set_id){
                    payload.break_data = null
                }
                this.study_break_data[this.selectedWorkId] = payload
                localStorageService.saveStudyBreakData(this.study_break_data)
            }
        },
        deleteStudyBreakData() {
            if(this.isInterrupt){
                this.study_break_data[this.selectedWorkId].temporary_data = null
                localStorageService.saveStudyBreakData(this.study_break_data)
            }
        },
        swapStudyBreakData(interrupt_path: string) {
            // NFR-5675 中断可能な学習かと該当するワークで中断データがあるのかチェック
            if (this.isInterrupt && this.study_break_data[this.selectedWorkId]) {
                const workBreaKData = this.study_break_data[this.selectedWorkId];
                // break_dataまたはtemporary_dataが存在するかを確認
                if (workBreaKData?.break_data || workBreaKData?.temporary_data) {
                    workBreaKData.break_data = workBreaKData.temporary_data;
                    // set abort datetime
                    workBreaKData.break_data.data_aborted_at = local_time();
                    // set interrupt path
                    workBreaKData.break_data.interrupt_path = interrupt_path;
                    // set interruption question index
                    workBreaKData.break_data.interrupt_index = this.q_idx;
                    workBreaKData.temporary_data = null;
                    localStorageService.saveStudyBreakData(this.study_break_data)
                }
            }
        },
        saveLatestQuestionHistories(payload){
            this.latest_question_histories = payload
        },
        saveIsWronglyAnswered(payload){
            this.is_wrongly_answered = payload
        },
    }
})